import { useEffect, useReducer, useRef } from 'react'

import { SelectItem } from '@mantine/core'
import sortBy from 'lodash/sortBy'

import { callApiNew } from 'api/axios'
import { formatExpertUser, formatSpecialty } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ExpertUserSummary } from 'interfaces/types'
import { Specialty } from 'interfaces/types/Specialty'
import { generateSelectItem } from 'utils/generateSelectItem'

type SearchState = {
  specialtyData: (Specialty & SelectItem)[]
  expertData: (ExpertUserSummary & SelectItem)[]
  selectedSpecialty?: (Specialty & SelectItem) | null
  selectedExpert?: (ExpertUserSummary & SelectItem) | null
}

type SearchAction =
  | { type: 'SET_DATA'; payload: SearchState }
  | { type: 'SET_SPECIALTY_DATA'; payload: (Specialty & SelectItem)[] }
  | { type: 'SET_EXPERT_DATA'; payload: (ExpertUserSummary & SelectItem)[] }
  | { type: 'SELECT_SPECIALTY'; payload: (Specialty & SelectItem) | null }
  | { type: 'SELECT_EXPERT'; payload: (ExpertUserSummary & SelectItem) | null }

const initialState: SearchState = {
  specialtyData: [],
  expertData: [],
  selectedSpecialty: null,
  selectedExpert: null,
}

const reducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, ...action.payload }
    case 'SET_SPECIALTY_DATA':
      return { ...state, specialtyData: action.payload }
    case 'SET_EXPERT_DATA':
      return { ...state, expertData: action.payload }
    case 'SELECT_SPECIALTY':
      return {
        ...state,
        selectedSpecialty: action.payload,
        selectedExpert: null,
      }
    case 'SELECT_EXPERT':
      return { ...state, selectedExpert: action.payload }
    default:
      return state
  }
}

const useSearch = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { specialtyData, expertData, selectedSpecialty, selectedExpert } = state
  const rawExpertData = useRef<any>()
  const { locale, router } = useRouterExtend()
  useEffect(() => {
    // Simulating the API call to fetch data
    const fetchData = async () => {
      try {
        const promiseOne = callApiNew(PATHS.EXPERT.GET_LIST_OF_SPECIALTIES)
        const promiseTwo = callApiNew(
          PATHS.EXPERT.GET_EXPERT_BY_NAME_AND_SPECIALTY,
        )
        const [specialtyResponse, expertResponse] = await Promise.all([
          promiseOne,
          promiseTwo,
        ])

        const specialties = generateSelectItem(
          sortBy(
            specialtyResponse?.data.specialty
              .map(formatSpecialty)
              .filter((i: Specialty) => i.countExperts > 0) as Array<Specialty>,
            ['slug'],
          ),
          'name',
          'name',
        )

        const expert = generateSelectItem(
          expertResponse?.data?.experts.map(
            formatExpertUser,
          ) as Array<ExpertUserSummary>,
          'displayName',
          'displayName',
        )

        rawExpertData.current = expert
        dispatch({
          type: 'SET_DATA',
          payload: {
            specialtyData: specialties,
            expertData: expert,
          },
        })
      } catch (error) {
        console.error('Error fetching address data:', error)
      }
    }

    fetchData()
  }, [])

  const handleSpecialtyChange = async (
    _selectedSpecialty: (Specialty & SelectItem) | null,
  ) => {
    dispatch({ type: 'SELECT_SPECIALTY', payload: _selectedSpecialty })

    if (!_selectedSpecialty) {
      dispatch({ type: 'SET_EXPERT_DATA', payload: rawExpertData.current })

      return
    }

    const response = await callApiNew(
      PATHS.EXPERT.GET_EXPERT_BY_NAME_AND_SPECIALTY,
      {
        params: {
          specialty: _selectedSpecialty.termId || '',
        },
      },
    )
    const expertData = generateSelectItem(
      response?.data?.experts.map(formatExpertUser) as Array<ExpertUserSummary>,
      'displayName',
      'displayName',
    )
    // Renew expert data
    dispatch({ type: 'SET_EXPERT_DATA', payload: expertData })
  }

  const handleExpertChange = async (
    _selectedExpert: ExpertUserSummary & SelectItem,
  ) => {
    dispatch({ type: 'SELECT_EXPERT', payload: _selectedExpert })
    router.push(`${getHelloSitesUrl(locale)}${_selectedExpert.profileUrl}`)
  }
  return {
    specialtyData,
    expertData,
    selectedSpecialty,
    selectedExpert,
    handleSpecialtyChange,
    handleExpertChange,
  }
}

export default useSearch
